import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { ADMIN_EMAIL } from '../../constants';
import { getReminderDays, setReminderDays, getPending, getLogs, addManual, sendNow } from '../../api/remindersApi';
import '../Profile.css';

const AdminReminders = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [reminderDays, setReminderDaysState] = useState('');
  const [savingDays, setSavingDays] = useState(false);
  const [pending, setPending] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ userEmail: '', userName: '', itemName: '', purchaseDate: '' });
  const [adding, setAdding] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    if (!user || user.email !== ADMIN_EMAIL) { navigate('/', { replace: true }); return; }
    const load = async () => {
      setLoading(true);
      try {
        const [daysData, pendingData, logsData] = await Promise.all([
          getReminderDays(), getPending(), getLogs()
        ]);
        setReminderDaysState(daysData.reminder_days ?? daysData.reminderDays ?? '');
        setPending(Array.isArray(pendingData) ? pendingData : []);
        setLogs(Array.isArray(logsData) ? logsData : (Array.isArray(logsData?.logs) ? logsData.logs : []));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, navigate]);

  const refreshData = useCallback(async () => {
    const [pendingData, logsData] = await Promise.all([getPending(), getLogs()]);
    setPending(Array.isArray(pendingData) ? pendingData : []);
    setLogs(Array.isArray(logsData) ? logsData : (Array.isArray(logsData?.logs) ? logsData.logs : []));
  }, []);

  if (!user || user.email !== ADMIN_EMAIL) return null;

  const handleSaveDays = async () => {
    setSavingDays(true);
    try { await setReminderDays(Number(reminderDays)); } finally { setSavingDays(false); }
  };

  const handleAddManual = async (e) => {
    e.preventDefault();
    if (!form.userEmail) return;
    setAdding(true);
    try {
      await addManual({ ...form });
      setForm({ userEmail: '', userName: '', itemName: '', purchaseDate: '' });
      await refreshData();
    } finally { setAdding(false); }
  };

  const handleSendNow = async (id) => {
    setSendingId(id);
    try { await sendNow(id); await refreshData(); } finally { setSendingId(null); }
  };

  const pendingManual = logs.filter(l => l.status === 'pending_manual');
  const allPending = [...pending, ...pendingManual];

  const statusLabel = { sent: 'נשלח', failed: 'נכשל', pending_manual: 'ממתין (ידני)' };

  return (
    <div className="history-page" dir="rtl">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>שלח תזכורת אחרי</span>
        <input
          type="number"
          min={1}
          value={reminderDays}
          onChange={e => setReminderDaysState(e.target.value)}
          style={{ width: 60, textAlign: 'center', border: '1px solid #ccc', borderRadius: 6, padding: '4px 8px' }}
        />
        <span style={{ fontWeight: 600 }}>ימים</span>
        <button className="rec-popup-submit" onClick={handleSaveDays} disabled={savingDays}>
          {savingDays ? 'שומר...' : 'שמור'}
        </button>
      </div>

      <div className="admin-rec-tabs">
        <button className={`admin-rec-tab ${tab === 'pending' ? 'active' : ''}`} onClick={() => setTab('pending')}>
          ממתינות לתזכורת ({allPending.length})
        </button>
        <button className={`admin-rec-tab ${tab === 'logs' ? 'active' : ''}`} onClick={() => setTab('logs')}>
          נשלחו ({logs.filter(l => l.status !== 'pending_manual').length})
        </button>
      </div>

      {loading && <div className="history-status">טוען...</div>}

      {!loading && tab === 'pending' && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  {['שם', 'מייל', 'שם תוכנית', 'תאריך קנייה', 'ימים עד לשליחה'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allPending.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#888' }}>אין ממתינות</td></tr>
                )}
                {allPending.map((row, i) => (
                  <tr key={row.id ?? i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 12px' }}>{row.name || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{row.email || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{row.item_name || row.itemName || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{row.purchase_date || row.purchaseDate || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {row.status === 'pending_manual'
                        ? 'ממתין (ידני)'
                        : row.days_until_reminder === 0
                          ? 'היום'
                          : row.days_until_reminder != null
                            ? `בעוד ${row.days_until_reminder} ימים`
                            : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <form onSubmit={handleAddManual} style={{ marginTop: 24, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <span style={{ fontWeight: 600, width: '100%' }}>+ הוספה ידנית</span>
            <input
              className="rec-popup-input"
              placeholder="מייל *"
              value={form.userEmail}
              onChange={e => setForm(f => ({ ...f, userEmail: e.target.value }))}
              required
              style={{ flex: '1 1 160px' }}
            />
            <input
              className="rec-popup-input"
              placeholder="שם"
              value={form.userName}
              onChange={e => setForm(f => ({ ...f, userName: e.target.value }))}
              style={{ flex: '1 1 120px' }}
            />
            <input
              className="rec-popup-input"
              placeholder="שם תוכנית"
              value={form.itemName}
              onChange={e => setForm(f => ({ ...f, itemName: e.target.value }))}
              style={{ flex: '1 1 140px' }}
            />
            <input
              className="rec-popup-input"
              type="date"
              placeholder="תאריך קנייה"
              value={form.purchaseDate}
              onChange={e => setForm(f => ({ ...f, purchaseDate: e.target.value }))}
              style={{ flex: '1 1 140px' }}
            />
            <button className="rec-popup-submit" type="submit" disabled={adding}>
              {adding ? 'מוסיף...' : 'הוסף'}
            </button>
          </form>
        </>
      )}

      {!loading && tab === 'logs' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {['שם', 'מייל', 'שם תוכנית', 'תאריך שליחה', 'סטטוס', ''].map((h, i) => (
                  <th key={i} style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.filter(l => l.status !== 'pending_manual').length === 0 && (
                <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: '#888' }}>אין רשומות</td></tr>
              )}
              {logs.filter(l => l.status !== 'pending_manual').map((row, i) => (
                <tr key={row.id ?? i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 12px' }}>{row.name || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>{row.email || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>{row.item_name || row.itemName || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>{row.sent_at || row.sentAt || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{ color: row.status === 'failed' ? '#e53935' : row.status === 'sent' ? '#0cad5e' : '#888' }}
                      title={row.error || ''}>
                      {statusLabel[row.status] || row.status}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {(row.status === 'failed' || row.status === 'pending_manual') && (
                      <button className="rec-popup-submit" style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => handleSendNow(row.id)} disabled={sendingId === row.id}>
                        {sendingId === row.id ? '...' : 'שלח עכשיו'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminReminders;
